import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { CURRENCY_OPTIONS } from '@/constants/currency';
import { normalizeTimezone, TIMEZONE_OPTIONS } from '@/constants/timezones';
import { useOrg } from '@/contexts/OrgContext';
import { useImportStatus } from '@/hooks/integrations/useImportStatus';
import { useProfile } from '@/hooks/profile/useProfile';
import { useLearnedContext } from '@/hooks/useLearnedContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Button } from '@/lib/base/buttons/button';
import { getCrmAggregateQueryKey } from '@/lib/crm/hooks/useCrmAggregate';
import { apiPatch, apiPut } from '@/lib/http/client';
import { logger } from '@/lib/logger';
import { getBrowserTimezone } from '@/lib/scheduling/timezone';
import { useDashboardMetrics } from '@/pages/dashboard/useDashboardMetrics';

import { useUser } from '../../allauth_auth/hooks.js';
import { createProposalClient } from '../../lib/proposals/core/ProposalClient';
import type {
  ProposalDetail,
  ProposalUIDefinition,
} from '../../lib/proposals/core/types';
import { useProposal } from '../../lib/proposals/hooks/useProposal';
import ProposalUIRenderer, {
  type ImportStatsData,
  InsightEditProvider,
} from './ProposalUIRenderer';
import { useProposalRefresh } from './useProposalRefresh';

interface WelcomeProposalContentProps {
  proposal: ProposalDetail;
  onUpdate: (action: ProposalDetail) => void;
  onComplete: (precomputedNextActionId?: number) => void;
  getNextActionId?: (currentId: number) => number | null;
}

const CONTACT_COUNT_BODY = {
  entity_type_name: 'ahoy_contact' as const,
  aggregations: [{ function: 'count' as const, alias: 'count' as const }],
} as const;

/**
 * Renders the welcome/onboarding proposal UI: import stats, timezone/currency
 * settings, insights, and a simple "Next" footer. Extracted from ProposalDetailPanelInner
 * so the main panel does not load onboarding hooks for every proposal type.
 *
 * Must be rendered inside ProposalRefreshProvider (provided by ProposalDetailPanel).
 */
export default function WelcomeProposalContent({
  proposal,
  onUpdate: _onUpdate,
  onComplete,
  getNextActionId,
}: WelcomeProposalContentProps) {
  const { triggerRefresh } = useProposalRefresh();

  // Use React Query cache to stay in sync with refreshed data
  const { data: queryAction } = useProposal(proposal.org, proposal.id);
  const currentAction = queryAction ?? proposal;

  const hasCustomUI = proposal.ui_definition && proposal.actions.length === 0;

  // Create proposal client for dismissal — memoized to avoid recreating on every render
  const proposalClient = useMemo(
    () => createProposalClient(proposal.org),
    [proposal.org]
  );

  // Org context
  const { currentOrg } = useOrg();
  const orgIdString = currentOrg?.id ? String(currentOrg.id) : '';

  // Import status — provider-agnostic; fetches jobs for all connected
  // providers (Gmail, Outlook, etc.) via /api/orgs/:orgId/import-jobs/.
  const importStatus = useImportStatus({ orgId: orgIdString });

  const { data: metricsData, isLoading: metricsLoading } = useDashboardMetrics(
    currentOrg?.id
  );

  // Fetch learning status to check if AI analysis is complete
  const user = useUser();
  const { isLearningComplete, loading: learningLoading } = useLearnedContext({
    orgId: currentOrg?.id,
    userId: user?.id,
    pollUntilComplete: true,
    pollInterval: 5000,
  });

  const { hasPermissionGroup } = usePermissions();
  const isPermissionsAdmin = hasPermissionGroup('ahoy_permissions_admin');
  const orgDefaultCurrency = currentOrg?.defaultCurrency ?? 'USD';

  // Fetch user profile for saved timezone. `timezone` is always resolved by
  // the backend (unset → "UTC"), so `timezone_is_set` is the signal for
  // whether the user actually chose one — only then does the picker preselect
  // the profile value instead of suggesting the browser-detected zone.
  const { profileData } = useProfile();
  const userTimezone = profileData?.user?.timezone_is_set
    ? profileData.user.timezone
    : undefined;

  // Detect browser timezone and normalize it to match TIMEZONE_OPTIONS
  const detectedTimezone = useMemo(() => {
    // Browser tz is only a *suggested default* for the picker the user
    // explicitly confirms — never used to render dates (see getBrowserTimezone).
    const browserTz = getBrowserTimezone();
    return normalizeTimezone(browserTz);
  }, []);

  // Settings state for timezone and currency
  const [selectedTimezone, setSelectedTimezone] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);
  const queryClient = useQueryClient();

  // Initialize timezone from profile or detected
  useEffect(() => {
    if (userTimezone && userTimezone.trim()) {
      const exists = TIMEZONE_OPTIONS.some(tz => tz.value === userTimezone);
      setSelectedTimezone(exists ? userTimezone : detectedTimezone);
    } else if (detectedTimezone) {
      setSelectedTimezone(detectedTimezone);
    }
  }, [userTimezone, detectedTimezone]);

  // Suggest currency based on timezone
  const suggestCurrencyFromTimezone = useCallback((tz: string): string => {
    if (tz === 'Europe/London') {
      return 'GBP';
    }
    if (
      tz.startsWith('Europe/') &&
      !['Europe/London', 'Europe/Moscow', 'Europe/Istanbul'].includes(tz)
    ) {
      return 'EUR';
    }
    return 'USD';
  }, []);

  // Initialize currency from org default, or suggest from timezone if not set
  useEffect(() => {
    if (orgDefaultCurrency && orgDefaultCurrency !== 'USD') {
      setSelectedCurrency(orgDefaultCurrency);
    } else if (isPermissionsAdmin && detectedTimezone) {
      setSelectedCurrency(suggestCurrencyFromTimezone(detectedTimezone));
    } else {
      setSelectedCurrency('USD');
    }
  }, [
    orgDefaultCurrency,
    isPermissionsAdmin,
    detectedTimezone,
    suggestCurrencyFromTimezone,
  ]);

  // Save settings handler
  const handleSaveSettings = useCallback(async () => {
    setIsSavingSettings(true);
    const errors: string[] = [];

    const timezoneToSave = selectedTimezone || detectedTimezone;
    try {
      await apiPut('/users/api/profile/', { timezone: timezoneToSave });
      queryClient.invalidateQueries({ queryKey: ['userProfile'] });
    } catch (err) {
      logger.error(
        'Failed to save timezone',
        err instanceof Error ? err : new Error(String(err)),
        { component: 'proposals', orgId: currentOrg?.id }
      );
      errors.push('timezone');
    }

    if (isPermissionsAdmin && selectedCurrency) {
      try {
        await apiPatch(`/orgs/api/orgs/${currentOrg?.id}/`, {
          default_currency: selectedCurrency,
        });
      } catch (err) {
        logger.error(
          'Failed to save currency',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'proposals', orgId: currentOrg?.id }
        );
        errors.push('currency');
      }
    }

    setIsSavingSettings(false);

    if (errors.length > 0) {
      toast.error(
        `We couldn't save your ${errors.join(' and ')} settings. You can update them later in settings.`
      );
    }
  }, [
    selectedTimezone,
    detectedTimezone,
    isPermissionsAdmin,
    selectedCurrency,
    currentOrg?.id,
    queryClient,
  ]);

  // Contact count is sourced from the canonical CRM metric (counts ahoy_contact
  // entities for the org).  An earlier implementation tried to short-circuit via
  // an `action_count` field on a hardcoded list of import-job proposal source
  // types; that was both provider-coupled (broke for new providers) and
  // redundant — the metric is provider-agnostic and updates within seconds of
  // the importer running.
  //
  // useDashboardMetrics has staleTime=5min and does not poll, so while imports
  // are actively running we invalidate it every 10s. Without this, the welcome
  // proposal can show a stale contactsCount for up to 5 minutes mid-import.
  // Invalidates only the contact-count aggregate (`ahoy_contact` count) to
  // avoid unnecessary refetches of pipeline and column aggregates.
  const { hasActiveImports } = importStatus;
  useEffect(() => {
    if (!hasActiveImports || !currentOrg?.id) {
      return;
    }
    const intervalId = window.setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: getCrmAggregateQueryKey(currentOrg.id, CONTACT_COUNT_BODY),
      });
    }, 10_000);
    return () => window.clearInterval(intervalId);
  }, [hasActiveImports, currentOrg?.id, queryClient]);

  const contactsFound = metricsData?.contactsCount ?? 0;
  const importStatsIsLoading = importStatus.isLoading || metricsLoading;
  const importStats: ImportStatsData = useMemo(
    () => ({
      emailsAnalyzed: importStatus.totalEmailsProcessed,
      contactsFound,
      // Meetings synced from THIS user's own calendar integrations (import
      // jobs are scoped to request.user), not an org-wide meeting count.
      meetingsFound: importStatus.totalMeetingsProcessed,
      isLoading: importStatsIsLoading,
    }),
    [
      importStatus.totalEmailsProcessed,
      contactsFound,
      importStatus.totalMeetingsProcessed,
      importStatsIsLoading,
    ]
  );

  // Poll for proposal updates when learning completes
  const prevIsLearningCompleteRef = useRef(isLearningComplete);
  useEffect(() => {
    const justCompleted =
      isLearningComplete && !prevIsLearningCompleteRef.current;
    prevIsLearningCompleteRef.current = isLearningComplete;

    if (!justCompleted) {
      return;
    }

    logger.debug(
      '[WelcomeProposalContent] Learning completed, polling for proposal updates'
    );

    const hasActualInsights = () => {
      const uiDef = currentAction.ui_definition;
      const components =
        uiDef?.components ?? (Array.isArray(uiDef) ? uiDef : null);
      if (!Array.isArray(components)) {
        return false;
      }
      const insightsSection = components.find(
        (section: { id?: string }) => section.id === 'insights-section'
      );
      const items = insightsSection?.props?.items ?? insightsSection?.items;
      if (!Array.isArray(items)) {
        return false;
      }
      const roleItem = items.find(
        (item: { term?: string }) => item.term === 'Role'
      );
      return roleItem?.definition && !roleItem.definition.includes('Analyzing');
    };

    if (hasActualInsights()) {
      logger.debug(
        '[WelcomeProposalContent] Already have insights, no polling needed'
      );
      return;
    }

    let pollCount = 0;
    const maxPolls = 20;
    const pollInterval = setInterval(async () => {
      pollCount++;
      logger.debug('[WelcomeProposalContent] Polling for insights', {
        pollCount,
        maxPolls,
      });

      try {
        const freshData = await triggerRefresh();
        const freshUiDef = freshData?.ui_definition;
        const freshComponents =
          freshUiDef?.components ??
          (Array.isArray(freshUiDef) ? freshUiDef : null);
        if (Array.isArray(freshComponents)) {
          const section = freshComponents.find(
            (s: { id?: string }) => s.id === 'insights-section'
          );
          const sectionItems = section?.props?.items ?? section?.items;
          if (section && Array.isArray(sectionItems)) {
            const roleItem = sectionItems.find(
              (item: { term?: string }) => item.term === 'Role'
            );
            if (
              roleItem?.definition &&
              !roleItem.definition.includes('Analyzing')
            ) {
              logger.debug(
                '[WelcomeProposalContent] Got insights, stopping poll'
              );
              clearInterval(pollInterval);
              return;
            }
          }
        }
      } catch (err) {
        logger.error(
          '[WelcomeProposalContent] Poll refresh failed',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'proposals' }
        );
      }

      if (pollCount >= maxPolls) {
        logger.debug('[WelcomeProposalContent] Max polls reached, stopping');
        clearInterval(pollInterval);
      }
    }, 3000);

    triggerRefresh();

    return () => clearInterval(pollInterval);
  }, [isLearningComplete, triggerRefresh, currentAction.ui_definition]);

  // Dismiss handler: save settings, reject proposal, navigate to next
  const handleWelcomeDismiss = useCallback(async () => {
    if (isDismissing) {
      return;
    }
    setIsDismissing(true);

    try {
      await handleSaveSettings();
    } catch (error) {
      logger.error(
        'Failed to save settings during welcome dismiss',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'proposals', location: 'welcome-dismiss' }
      );
    }

    const nextId = getNextActionId?.(proposal.id) ?? null;

    try {
      await proposalClient.reject(proposal.id, {
        reason: 'Dismissed after review',
      });
      onComplete(nextId ?? undefined);
    } catch (error) {
      logger.error(
        'Failed to dismiss welcome proposal',
        error instanceof Error ? error : new Error(String(error)),
        { component: 'proposals', location: 'WelcomeProposalContent' }
      );
      setIsDismissing(false);
    }
  }, [
    isDismissing,
    handleSaveSettings,
    getNextActionId,
    proposal.id,
    onComplete,
    proposalClient,
  ]);

  // Persist a single user-edited insight to the learning profile.
  // Auto-save fires on blur from EditableDefinitionRow inside the renderer.
  // The renderer reverts its local draft if this rejects, so we surface the
  // failure as a toast and re-throw so the row sees the rejection.
  const userId = user?.id;
  const saveInsight = useCallback(
    async (term: string, value: string) => {
      // Reject (don't silently no-op) when context isn't ready so the
      // editable row reverts its draft instead of treating the edit as saved.
      if (!currentOrg?.id || !userId) {
        toast.error(`Couldn't save ${term}. Try again in a moment.`);
        throw new Error(
          'Cannot save insight: org or user context is unavailable.'
        );
      }
      try {
        await apiPatch(
          `/api/orgs/${currentOrg.id}/learning/users/${userId}/insights/`,
          { term, value }
        );
      } catch (err) {
        logger.error(
          'Failed to save insight edit',
          err instanceof Error ? err : new Error(String(err)),
          { component: 'proposals', orgId: currentOrg.id, term }
        );
        toast.error(`Couldn't save ${term}. Try again in a moment.`);
        throw err;
      }
    },
    [currentOrg?.id, userId]
  );

  const handleTimezoneChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedTimezone(e.target.value);
    },
    []
  );

  const handleCurrencyChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setSelectedCurrency(e.target.value);
    },
    []
  );

  let dismissButtonLabel = 'Next';
  if (isSavingSettings) {
    dismissButtonLabel = 'Saving…';
  } else if (isDismissing) {
    dismissButtonLabel = 'Finishing…';
  }

  return (
    <>
      {/* Content area */}
      <div className='flex-1 flex flex-col overflow-hidden relative bg-[var(--color-bg-secondary)]'>
        <div className='absolute inset-0 pointer-events-none' />

        {/* White fade gradient - masks dotted background behind heading */}
        {hasCustomUI && (
          <div
            className='absolute inset-x-0 top-0 h-[100px] pointer-events-none z-[1]'
            style={{
              background:
                'linear-gradient(to bottom, var(--color-bg-primary) 0%, var(--color-bg-primary) 40%, transparent 100%)',
            }}
          />
        )}

        {hasCustomUI && (
          <div className='flex-1 overflow-y-auto overflow-x-hidden relative z-[2]'>
            <div className='relative p-3 sm:p-4 lg:p-6'>
              <InsightEditProvider value={saveInsight}>
                <ProposalUIRenderer
                  uiDefinition={
                    (currentAction.ui_definition ??
                      proposal.ui_definition) as ProposalUIDefinition
                  }
                  importStats={importStats}
                  renderAfterComponent={{
                    afterId: 'insights-section',
                    content: (
                      <div className='rounded-lg border border-border-secondary bg-bg-primary overflow-hidden mb-4'>
                        {/* Timezone row */}
                        <div className='grid grid-cols-[auto_1fr] gap-x-3 py-2.5 px-4 border-b border-border-secondary'>
                          <label
                            htmlFor='tz-select'
                            className='text-sm font-semibold text-fg-primary whitespace-nowrap'
                          >
                            Your timezone
                          </label>
                          <div className='flex justify-end min-w-0'>
                            <select
                              id='tz-select'
                              value={selectedTimezone}
                              onChange={handleTimezoneChange}
                              disabled={isSavingSettings}
                              className="text-sm font-medium text-fg-primary bg-transparent border-none cursor-pointer focus:outline-none focus:ring-0 text-right appearance-none pr-4 min-w-0 max-w-full truncate bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%23666%22%20d%3D%22M3%204.5L6%207.5L9%204.5%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-right"
                            >
                              {TIMEZONE_OPTIONS.filter(
                                tz => tz.value !== ''
                              ).map(tz => (
                                <option key={tz.value} value={tz.value}>
                                  {tz.label}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Currency row - only for permissions admins */}
                        {isPermissionsAdmin && (
                          <div className='grid grid-cols-[auto_1fr] gap-x-3 py-2.5 px-4'>
                            <label
                              htmlFor='currency-select'
                              className='text-sm font-semibold text-fg-primary whitespace-nowrap'
                            >
                              Currency
                            </label>
                            <div className='flex justify-end min-w-0'>
                              <select
                                id='currency-select'
                                value={selectedCurrency}
                                onChange={handleCurrencyChange}
                                disabled={isSavingSettings}
                                className="text-sm font-medium text-fg-primary bg-transparent border-none cursor-pointer focus:outline-none focus:ring-0 text-right appearance-none pr-4 min-w-0 max-w-full truncate bg-[url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20fill%3D%22%23666%22%20d%3D%22M3%204.5L6%207.5L9%204.5%22%2F%3E%3C%2Fsvg%3E')] bg-no-repeat bg-right"
                              >
                                {CURRENCY_OPTIONS.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        )}
                      </div>
                    ),
                  }}
                />
              </InsightEditProvider>

              {/* Analyzing indicator - shown while learning is in progress */}
              {(!isLearningComplete || learningLoading) && (
                <div className='flex items-center justify-center gap-2 mt-4 py-3'>
                  <svg
                    className='size-4 animate-spin text-fg-brand-primary'
                    viewBox='0 0 24 24'
                    fill='none'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='3'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
                    />
                  </svg>
                  <span className='text-sm text-fg-secondary'>
                    Analyzing your account…
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Welcome footer - simple "Next" button instead of StickyFooter */}
      <div className='flex-shrink-0 border-t border-border-secondary bg-bg-primary pb-[env(safe-area-inset-bottom)]'>
        <div className='px-3 sm:px-4 lg:px-6 py-2 min-h-[100px] sm:min-h-0 flex flex-col justify-end'>
          <div className='flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 sm:gap-4'>
            <Button
              color='primary'
              size='md'
              isDisabled={isSavingSettings || isDismissing}
              onClick={handleWelcomeDismiss}
            >
              {dismissButtonLabel}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
